import { RecoveryRecordBuilder } from "ags/integration/builders/records/recovery-record-builder";
import { HoldAndSpinSchema, AGSSpinSchema } from "ags/integration/schema/ags-spin-response-schema";
import llHoldAndSpinRecordBuilder from "integration/builders/records/ll-hold-and-spin-record-builder";
import { MCAnticipationAuthResponseSchema } from "integration/schema/mc-auth-response-schema";
import GatewayResult, { GatewayType } from "model/gameplay/records/results/gateway-result";
import Gameplay from "slotworks/model/gameplay/gameplay";
import Record from "slotworks/model/gameplay/records/record";
import BonusResult from "slotworks/model/gameplay/records/results/bonus-result";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import { RecordBuilder } from "slotworks/server/builders/record-builder";
import bonusSymbolSetup from "utils/bonus-symbol-setup";
import mcPickRecordGridGenerator from "utils/mc-pick-record-grid-generator";
import attachMCSymbols from "utils/mc-symbol-grid-conversions";
import mcSpinRecordBuilder from "./mc-spin-record-builder";
import { LLWildBonusResult } from "integration/builders/result/ll-wild-bonus-result-builder";
import { HoldAndSpinRecord } from "ags/integration/records/hold-and-spin-record";

export class CGRecoveryBuilder extends RecoveryRecordBuilder implements RecordBuilder {

    public id: string = "MCRecoveryBuilder";

    protected spinRecordBuilderInstance = mcSpinRecordBuilder;

    public build(gameplay: Gameplay, request: any, response: any): Record[] {
        const records = super.build(gameplay, request, response);

        const authResponse = JSON.parse(response.data) as MCAnticipationAuthResponseSchema;
        const publicState = authResponse.publicState;
        const recoveredState = publicState.recoveredGame;
        const spinData = recoveredState.spin as any;
        const childFeature = spinData.childFeature as HoldAndSpinSchema[];

        bonusSymbolSetup.setAnticipationSymbols(publicState.stakes.availableStakes, publicState.anticipationSymbols);

        if (!spinData.MCSymbols) {
            spinData.MCSymbols = [];
        }

        const spinRecord = records[0] as SpinRecord;
        const mcBonusRecord = this.createMcBonusRecord(spinRecord, childFeature, gameplay, spinData);

        if (mcBonusRecord) {
            records.push(...mcBonusRecord);
        }

        // Setting the cash won on the first mc bonus has to be done manually.
        if (this.hasFirstPickBonusCompleted(childFeature)) {
            const bonusResults = spinRecord.getResultsOfType(BonusResult);
            const firstBonus = bonusResults.filter((result) => result.id === "pick").pop();
            // const pickResults = records.filter((record) => record instanceof PickMatchRecord);
            // firstBonus.cashWon = pickResults.find((result) => result.cashWon !== 0 || (result as any).complete === true).cashWon;
            firstBonus.played = true;
        }

        if (spinRecord instanceof SpinRecord && spinRecord.grid) {
            attachMCSymbols(spinData.MCSymbols, spinRecord);
        }

        if (this.hasPendingGateway(spinRecord, childFeature)) {
            // Reset to the root spin record to force gateway.
            const mcBonusWin = this.getMCBonusWin(childFeature);
            spinRecord.cashWon += mcBonusWin;
            return [spinRecord];
        }

        if (this.needDummyHoldAndSpinRecord(childFeature)) {
            const dummyHoldAndSpin = new HoldAndSpinRecord();
            dummyHoldAndSpin.cashWon = spinData.totalSpinWin - spinData.sumWins;
            const indexOfFirstHoldAndSpin = records.findIndex((record) => record instanceof HoldAndSpinRecord);
            records.splice(indexOfFirstHoldAndSpin, 0, dummyHoldAndSpin);
        }

        return records;
    }

    protected getMCBonusWin(childFeature: HoldAndSpinSchema[]) {
        const pickRecords = childFeature.filter((child) => child.type === "pick");
        let bonusWin = 0;

        pickRecords.forEach((child) => {
            if (child.cashWon) {
                bonusWin += child.cashWon;
            }
        });

        return bonusWin;
    }

    /**
     * This is only ever true if we're 3 or more spins into in the second of two FB bonuses. Meaning that the first
     * bonuses cash won amount is not returned by the server during recovery (the server only sends the last 3).
     */
    protected needDummyHoldAndSpinRecord(childFeature: HoldAndSpinSchema[]) {
        const areFreespins = childFeature.some((pick) => pick.type === "freeSpin");

        if (areFreespins) {
            return false;
        }

        const isSecondFortuneBlast = childFeature.some((pick) => pick.BonusNumber === 1);
        const hasNoCashWon = childFeature.every((pick) => !pick.cashWon);

        return isSecondFortuneBlast && hasNoCashWon;
    }

    /**
     * Each pick response includes a BonusNumber that isn't an ID for the bonus, it's a flag that is 1 when the first
     * bonus is complete. It's even 1 for the last pick of the first bonus. Any time this value is 1 we assume the first
     * bonus is played.
     */
    protected hasFirstPickBonusCompleted(childFeature: HoldAndSpinSchema[]) {
        return childFeature.some((child) => child.BonusNumber === 1);
    }

    protected hasPendingGateway(spinRecord: SpinRecord, childFeature: HoldAndSpinSchema[]) {
        const freespinsHaveStarted = childFeature.some((feature) => feature.type === "freeSpin");

        if (freespinsHaveStarted) {
            return false;
        }

        const firstPickIsComplete = childFeature.some((feature) => feature.BonusNumber === 1);
        const picksFromFirstBonusExist = childFeature.some((feature) => feature.BonusNumber === 0);
        const isEndOfFirstPick = firstPickIsComplete && picksFromFirstBonusExist;

        const bonusResults = spinRecord.results.filter((result) => {
            const isGateway = result instanceof GatewayResult;
            const isWildBonus = result instanceof LLWildBonusResult;
            return isGateway || isWildBonus;
        });

        const hasSecondBonusGateway = bonusResults.length >= 2;

        return isEndOfFirstPick && hasSecondBonusGateway;
    }

    protected createMcBonusRecord(spinRecord: SpinRecord, childFeature: HoldAndSpinSchema[], gameplay: Gameplay, spinData: AGSSpinSchema) {

        const childFeaturesForGame = mcPickRecordGridGenerator.getMostRecentChildFeatures(spinRecord, childFeature);
        const hasChildFeatures = childFeaturesForGame.length > 0;
        const featureHasNotBeenPlayed = childFeaturesForGame.every((pickRecord) => !pickRecord.cashWon);
        const pickRecords = childFeaturesForGame.map((schema) => llHoldAndSpinRecordBuilder.buildRecord(schema, gameplay, spinData));
        const areFreespinRecords = childFeature.some((feature) => feature.type === "freeSpin");
        const records = [];

        if (hasChildFeatures && featureHasNotBeenPlayed && !areFreespinRecords) {

            if (pickRecords) {
                spinRecord.hasChildren = true;
            }

            records.push(...pickRecords);
        }

        return records;
    }
}

export default new CGRecoveryBuilder();
